/**
 * mapColors: the capacity map's marker palette, the paper-base filter
 * value, and the checks that keep the markers legible against it
 * (LeafletMap's `.map-tile-muted` class, src/index.css). One home for
 * every hex the map draws, so a future change to one marker color can't
 * silently break another's contrast or distinguishability — pinned by
 * tests/ui/mapColors.test.ts.
 *
 * The base map is a paper-quiet, near-monochrome treatment (CARTO
 * Positron / Mapbox Light's manner), and every color an operator would
 * want a say over (a unit's dot, the client hue, the prospect hue, the
 * placement pin, the directory map's provider hue) has a configured
 * value behind it instead of a code
 * constant — see DEFAULT_MAP_COLORS and UNIT_COLOR_SWATCHES below, and
 * the GET /map/colors + PUT /admin/map/colors + PUT
 * /admin/units/{id}/color routes (api/src/functions/mapColors.ts) that
 * read and write them.
 */

/**
 * The base-tile filter (src/index.css `.map-tile-muted`), duplicated here
 * as a string so a test can pin the exact value src/index.css declares —
 * see tests/ui/mapColors.test.ts, which reads index.css's raw text and
 * asserts it contains this literal.
 */
export const MAP_TILE_FILTER = 'grayscale(0.85) sepia(0.18) brightness(1.04) contrast(0.88) saturate(0.9)';

/**
 * The base map's approximate flat grey once `.map-tile-muted` is applied —
 * MAP_TILE_FILTER run (by hand, via the CSS Filter Effects spec's color
 * matrices for grayscale/sepia/saturate and the plain per-channel scale for
 * brightness/contrast — see tools/ scratch derivation, not checked into
 * the repo) against a neutral mid-grey (#DCDCDC, matching no hue of its
 * own, so only the filter's own warmth shows): #DCDCDC -> #E5E0D7. That
 * warm near-white is what "paper" means here — every marker is checked
 * against IT, not the raw tile, because that's what a viewer actually
 * sees under the pins.
 *
 * The same filter run against a typical light-road-style highway orange
 * (~#F6A96A) lands at ~#C6BAAC — a muted taupe with no orange left in
 * it, which is the actual fix for the founder's "highways read orange"
 * complaint (the filter, not a marker choice, does that work).
 *
 * No Azure Maps key was available in this environment to render a live
 * tile and sample it directly; both reference colors above are
 * approximations of Azure Maps' microsoft.base.road light tileset (typed
 * out "by the numbers" per the CLAUDE.md tune-without-a-key path), so a
 * live-tile spot-check is worth doing once a key is available, but the
 * filter's own math is exact and reproducible from MAP_TILE_FILTER.
 */
export const MUTED_BASE_GREY = '#E5E0D7';

/** Every marker color must clear this against MUTED_BASE_GREY (WCAG's
 *  non-text 3:1 floor — the smallest ratio at which a filled shape reads
 *  as distinct from its background). */
export const MIN_MARKER_CONTRAST = 3;

/** Every pair of colors meant to read as different things must clear this
 *  CIE76 ΔE. Under ~10 is imperceptible-to-subtle at a glance; this
 *  project's palette clears each pair by well over 2x the floor. */
export const MIN_MARKER_DELTA_E = 15;

/**
 * Client markers on the Clients layer: the DEFAULT hue for a tenant that
 * hasn't chosen its own (tenants.config map.colors.clients, read through
 * a small seam — api/_shared/mapColorsConfig.ts). One hue for every
 * client pin, distinct from every care-manager unit color and from the
 * unit palette below — a slate blue is outside both the green/gold/rust
 * unit-swatch family and any one unit's own color, so a client pin never
 * gets read as "which unit."
 */
export const CLIENT_MAP_PIN_COLOR = '#4A5578';

/**
 * The "place a client" pin: the DEFAULT color for a tenant that hasn't
 * chosen its own (tenants.config map.colors.placement_pin). Forest — the
 * brand anchor color, and, against the paper base, the darkest thing the
 * map draws, exactly as the founder asked ("make the placement pin itself
 * the darkest thing on the map").
 */
export const PLACEMENT_PIN_COLOR = '#1B4332';

/**
 * Provider-organization markers on the resource directory's map: the
 * DEFAULT hue for a tenant that hasn't chosen its own
 * (tenants.config map.colors.providers). A rust brown: a fill of its own,
 * outside the client slate and the placement-pin forest it can share a
 * map with ("near a client", "my clients"), and warm enough to read as
 * "a place you can send someone" rather than a person.
 */
export const PROVIDER_MAP_PIN_COLOR = '#8C4A2F';

/**
 * People on the resource directory's map: the DEFAULT hue for a tenant
 * that hasn't chosen its own (tenants.config map.colors.people). A deep
 * teal, clear of the provider rust it sits beside, the client slate and
 * the placement forest. A person and a place are different answers to a
 * care manager's question, so they get different colors rather than the
 * same color drawn hollow.
 */
export const PEOPLE_MAP_PIN_COLOR = '#2F6F7A';

/** Every configurable tenant map color, resolved. Mirrors the API-side
 *  reader's defaults (api/_shared/mapColorsConfig.ts DEFAULT_MAP_COLORS)
 *  exactly — kept in lockstep so a tenant that has never saved sees the
 *  same colors the server would hand back. Prospects share the CLIENT hue
 *  (they're rendered hollow — MapPin.filled: false — not a separate
 *  color; a prospect and a client are the same "not yet placed" class of
 *  thing at different stages, not two hues to tell apart). */
export const DEFAULT_MAP_COLORS = {
  clients: CLIENT_MAP_PIN_COLOR,
  prospects: CLIENT_MAP_PIN_COLOR,
  placement_pin: PLACEMENT_PIN_COLOR,
  providers: PROVIDER_MAP_PIN_COLOR,
  people: PEOPLE_MAP_PIN_COLOR,
} as const;

/**
 * A care manager with no unit at all (unit_id null — not merely a unit
 * with no color set, which falls back to workspaceFormat.ts's
 * unitDotColor palette instead). A periwinkle blue-violet: a pleasant hue
 * of its own, outside every family the unit swatches and the fallback
 * palette use (its nearest, Plum, is ΔE76 25.6 away), so "no unit" never
 * reads as a real unit, and 4.3:1 against MUTED_BASE_GREY. It is the
 * ring, and the fill when she is Open, like any unit color.
 */
export const UNASSIGNED_CM_COLOR = '#5560B0';

/**
 * The centre of an on-leave care manager's pin: her unit color stays on
 * the ring, the middle goes a light neutral grey. (An Open pin's centre is
 * the unit color; Talk to Me and Not Open take their stance fills,
 * src/lib/capacity.ts STANCE_FILL.) A hex because Leaflet writes SVG
 * attributes (the raw-grey lint bans grey class names, not values). It is
 * a centre inside a ring, not a marker on the map, so it is exempt by role
 * from the base-map contrast floor (1.27:1 against MUTED_BASE_GREY, by
 * design); it stays ΔE 15 or more from every unit color and every stance
 * fill, so on leave is never confused with a stance.
 */
export const CM_ON_LEAVE_FILL = '#C8C8C8';

/**
 * A care manager's ring width. The unit is carried by the ring's area, not
 * its contrast: a pale unit (the palest code color is 1.44:1 against the
 * grey centre) reads as its hue when the band is three pixels wide at pin
 * size, where two pixels does not. Hover and the selected pin go one step
 * heavier.
 */
export const CM_RING_WEIGHT = 3;
export const CM_RING_HOVER_WEIGHT = 4;

/**
 * The picker's eight curated swatches (Admin > Units > Map colours, and
 * the same picker for the tenant's configurable colors) — founder
 * ask: pick from a small set that's already on-brand, or type a hex.
 * Forest/Sage/Gold are the brand's own primary + accent colors
 * (docs/brand-guidelines/SKILL.md); the other five extend the family
 * into hues the brand palette doesn't cover, so eight units can each get
 * a genuinely different color. The terracotta is deliberately NOT SPICE's
 * Pepper Red (#C1512D) — that hue is reserved for the SPICE sub-brand.
 */
export const UNIT_COLOR_SWATCHES: ReadonlyArray<{ label: string; hex: string }> = [
  { label: 'Forest', hex: '#1B4332' },
  { label: 'Sage', hex: '#52796F' },
  { label: 'Gold', hex: '#B8893D' },
  { label: 'Plum', hex: '#5B3A6B' },
  { label: 'Slate blue', hex: '#34557A' },
  { label: 'Terracotta', hex: '#A65D3B' },
  { label: 'Teal', hex: '#2A7F7A' },
  { label: 'Mulberry', hex: '#8A3A5B' },
];

/** A 1.5px sage line at 70% opacity — the "lines to clients" distance
 *  rays a selected care manager draws to their clients (LeafletMap's
 *  polyline layer). Flat, not per-unit: with client pins reduced to one
 *  hue, coloring the rays per-unit would reintroduce the exact busyness
 *  the paper base removed. Thin and light on purpose: the ray is a
 *  distance cue between two pins, not a mark in its own right, so it
 *  reads underneath the pins rather than competing with them. */
export const DISTANCE_RAY_COLOR = '#52796F';
export const DISTANCE_RAY_WEIGHT = 1.5;
export const DISTANCE_RAY_OPACITY = 0.7;

const HEX_RE = /^#[0-9A-Fa-f]{6}$/;

/** True for a well-formed 6-digit hex color ("#1B4332"); false for
 *  anything else (missing '#', 3-digit shorthand, named colors, etc — the
 *  picker's free-hex field and the API's validator both refuse those). */
export function isValidHexColor(value: string): boolean {
  return HEX_RE.test(value);
}

export function hexToRgb(hex: string): [number, number, number] {
  const h = hex.replace('#', '');
  return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
}

function srgbToLinear(c: number): number {
  const v = c / 255;
  return v <= 0.04045 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
}

/** Relative luminance (WCAG 2.x definition), 0..1. */
function relativeLuminance(hex: string): number {
  const [r, g, b] = hexToRgb(hex).map(srgbToLinear);
  return 0.2126 * r! + 0.7152 * g! + 0.0722 * b!;
}

/** WCAG contrast ratio between two colors, 1..21. Order doesn't matter. */
export function contrastRatio(hexA: string, hexB: string): number {
  const lA = relativeLuminance(hexA);
  const lB = relativeLuminance(hexB);
  const lighter = Math.max(lA, lB);
  const darker = Math.min(lA, lB);
  return (lighter + 0.05) / (darker + 0.05);
}

const D65 = { x: 0.95047, y: 1, z: 1.08883 };

function xyzF(t: number): number {
  const delta = 6 / 29;
  return t > delta ** 3 ? Math.cbrt(t) : t / (3 * delta * delta) + 4 / 29;
}

/** CIE L*a*b* from sRGB hex, via linear-light XYZ (D65). */
function hexToLab(hex: string): [number, number, number] {
  const [r, g, b] = hexToRgb(hex).map(srgbToLinear);
  const x = r! * 0.4124 + g! * 0.3576 + b! * 0.1805;
  const y = r! * 0.2126 + g! * 0.7152 + b! * 0.0722;
  const z = r! * 0.0193 + g! * 0.1192 + b! * 0.9505;
  const fx = xyzF(x / D65.x);
  const fy = xyzF(y / D65.y);
  const fz = xyzF(z / D65.z);
  return [116 * fy - 16, 500 * (fx - fy), 200 * (fy - fz)];
}

/** CIE76 color-difference (ΔE*ab) between two sRGB hex colors. Simple and
 *  good enough for "can a person tell these apart at a glance" — this is
 *  a marker palette, not a print-proofing pipeline. */
export function deltaE76(hexA: string, hexB: string): number {
  const [l1, a1, b1] = hexToLab(hexA);
  const [l2, a2, b2] = hexToLab(hexB);
  return Math.sqrt((l1 - l2) ** 2 + (a1 - a2) ** 2 + (b1 - b2) ** 2);
}

/** True when `hex` would be hard to make out against the paper base — the
 *  Admin picker's save-time contrast check. A WARNING, not a block (the
 *  picker still lets Champagne Gold — an accent color the brand guide
 *  itself calls out as "never for body text" — through with a caution,
 *  the same way the brand guide treats it everywhere else). */
export function contrastWarns(hex: string, base: string = MUTED_BASE_GREY): boolean {
  return contrastRatio(hex, base) < MIN_MARKER_CONTRAST;
}

/** One named color for closeColorPairs' input — `key` is whatever the
 *  caller wants back in the warning (a unit name, "Clients", ...). */
export interface NamedColor {
  key: string;
  hex: string;
}

/** Every pair of the given colors closer than MIN_MARKER_DELTA_E — the
 *  Admin picker's save-time "these two are hard to tell apart" warning,
 *  checked across EVERY color chosen together (every unit plus the three
 *  tenant colors), not just within one fixed palette. A warning, not a
 *  block: two units may legitimately want near-identical colors (e.g. a
 *  unit being retired and its replacement), and the picker's job is to
 *  say so, not to forbid it. */
export function closeColorPairs(colors: readonly NamedColor[]): Array<{ a: string; b: string; deltaE: number }> {
  const pairs: Array<{ a: string; b: string; deltaE: number }> = [];
  for (let i = 0; i < colors.length; i++) {
    for (let j = i + 1; j < colors.length; j++) {
      const a = colors[i]!;
      const b = colors[j]!;
      const d = deltaE76(a.hex, b.hex);
      if (d < MIN_MARKER_DELTA_E) pairs.push({ a: a.key, b: b.key, deltaE: d });
    }
  }
  return pairs;
}
